import type { ErrorInfo, ReactNode } from 'react'
import { Component } from 'react'
import { isReloadInFlight, isStaleChunkError } from '../lib/staleChunk'
import { Button } from './Button'
import { CodeBlock } from './CodeBlock'

interface Props {
  children: ReactNode
  /** Reset the boundary when this changes, so navigation clears a stuck screen. */
  resetKey?: unknown
  fallbackTitle?: string
}

interface State {
  error: Error | null
}

/**
 * Catches a render crash so one broken panel does not take the console with it.
 * A network or API failure is not this: those are ErrorState, on the page.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Render failed', error, info.componentStack)
  }

  override componentDidUpdate(previous: Props): void {
    if (this.state.error && previous.resetKey !== this.props.resetKey) {
      this.setState({ error: null })
    }
  }

  override render(): ReactNode {
    const { error } = this.state
    if (!error) return this.props.children

    // A chunk that failed to load is not a crash in the page, and saying
    // "something threw an error while drawing itself" sends somebody debugging
    // the wrong thing. Two ways to get here: the tab was open across a deploy
    // and the automatic reload already spent its one attempt, or the network
    // dropped mid-navigation. The wording covers both, in that order, because
    // that is their order of likelihood.
    if (isStaleChunkError(error)) {
      // The recovery reload does not stop this document: it keeps rendering for
      // the length of a round trip, which on a slow connection is long enough to
      // read. Presenting a fix that is already on its way as a failure is the
      // thing that sends somebody debugging a console that is about to be fine.
      if (isReloadInFlight()) {
        return (
          <div className="mx-auto max-w-xl space-y-3 py-16 text-center">
            <span className="mx-auto block h-6 w-6 animate-spin rounded-full border-2 border-line-strong border-t-accent" />
            <h1 className="text-xl font-semibold text-ink">Updating the console</h1>
            <p className="text-sm leading-relaxed text-ink-muted">
              A new version was released while this tab was open, so the page is reloading itself.
              Nothing you did caused it and no data changed.
            </p>
          </div>
        )
      }

      return (
        <div className="mx-auto max-w-xl space-y-4 py-16">
          <div className="space-y-1.5">
            <h1 className="text-xl font-semibold text-ink">A part of the console could not be loaded</h1>
            <p className="text-sm leading-relaxed text-ink-muted">
              Either the console was updated while this tab was open, in which case reloading picks up
              the new version, or the connection to the server dropped mid-click, in which case check
              the network and then reload. Nothing you did caused it and no data changed.
            </p>
          </div>
          <Button variant="primary" onClick={() => window.location.reload()}>
            Reload the page
          </Button>
        </div>
      )
    }

    return (
      <div className="mx-auto max-w-xl space-y-4 py-16">
        <div className="space-y-1.5">
          <h1 className="text-xl font-semibold text-ink">
            {this.props.fallbackTitle ?? 'This screen stopped working'}
          </h1>
          <p className="text-sm leading-relaxed text-ink-muted">
            Something in the page threw an error while drawing itself. Nothing you did caused data to
            change. Reload to get moving again, and send the detail below if it keeps happening.
          </p>
        </div>
        <CodeBlock code={`${error.name}: ${error.message}\n${error.stack ?? ''}`} title="Detail" wrap />
        <div className="flex gap-2">
          <Button variant="primary" onClick={() => window.location.reload()}>
            Reload the page
          </Button>
          <Button variant="secondary" onClick={() => this.setState({ error: null })}>
            Try rendering again
          </Button>
        </div>
      </div>
    )
  }
}
